import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';
import { compressImage, sanitizeFileName, isImageFile } from '../utils/imageUtils';

/**
 * Uploads a file to Firebase Storage
 * @param file - File to upload
 * @param path - Storage path (e.g., 'listings/{listingId}/images/')
 * @param compress - Whether to compress images (default: true)
 * @returns Download URL of the uploaded file
 */
export const uploadFile = async (
  file: File,
  path: string,
  compress: boolean = true
): Promise<string> => {
  try {
    let fileToUpload = file;
    const fileName = sanitizeFileName(file.name);

    // Compress images if requested
    if (compress && isImageFile(file)) {
      fileToUpload = await compressImage(fileToUpload);
    }

    // Create storage reference
    const storageRef = ref(storage, `${path}${fileName}`);

    // Upload file
    await uploadBytes(storageRef, fileToUpload);

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  } catch (error: any) {
    console.error('Error uploading file:', error);
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};

/**
 * Uploads multiple files to Firebase Storage
 * @param files - Array of files to upload
 * @param path - Storage path (e.g., 'listings/{listingId}/images/')
 * @param compress - Whether to compress images (default: true)
 * @returns Array of download URLs
 */
export const uploadMultipleFiles = async (
  files: File[],
  path: string,
  compress: boolean = true
): Promise<string[]> => {
  try {
    const uploadPromises = files.map((file) => uploadFile(file, path, compress));
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error: any) {
    console.error('Error uploading multiple files:', error);
    throw new Error(`Failed to upload files: ${error.message}`);
  }
};

/**
 * Deletes a file from Firebase Storage
 * @param fileUrl - Download URL of the file to delete
 * @returns True if deletion was successful
 */
export const deleteFile = async (fileUrl: string): Promise<boolean> => {
  try {
    // Extract the path from the URL
    // Firebase Storage URLs format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
    const url = new URL(fileUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
    
    if (!pathMatch) {
      throw new Error('Invalid file URL');
    }

    const filePath = decodeURIComponent(pathMatch[1]);
    const storageRef = ref(storage, filePath);

    await deleteObject(storageRef);
    return true;
  } catch (error: any) {
    console.error('Error deleting file:', error);
    throw new Error(`Failed to delete file: ${error.message}`);
  }
};

/**
 * Deletes multiple files from Firebase Storage
 * @param fileUrls - Array of download URLs to delete
 * @returns True if all deletions were successful
 */
export const deleteMultipleFiles = async (fileUrls: string[]): Promise<boolean> => {
  try {
    const deletePromises = fileUrls.map((url) => deleteFile(url));
    await Promise.all(deletePromises);
    return true;
  } catch (error: any) {
    console.error('Error deleting multiple files:', error);
    throw new Error(`Failed to delete files: ${error.message}`);
  }
};
